/**
 * Admin Affiliates Page for trAInedup.ai
 * Management interface for affiliate program
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Filter,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  RefreshCw,
  MoreVertical
} from 'lucide-react';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Input } from '../components/common/Input';
import { adminService } from '../services/firebase/admin';
import { AffiliateApprovalList } from '../components/admin/AffiliateApprovalList';

export const AdminAffiliatesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'pending' | 'active' | 'rejected'>('pending');
  const [loading, setLoading] = useState(false);
  const [affiliates, setAffiliates] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchAffiliates = useCallback(async () => {
    setLoading(true);
    try {
      let data = [];
      if (activeTab === 'active') {
        data = await adminService.getAffiliates();
      } else if (activeTab === 'rejected') {
        data = await adminService.getAffiliateApplications('rejected');
      }
      // Pending is handled by AffiliateApprovalList component
      setAffiliates(data);
    } catch (error) {
      console.error('Error fetching affiliates:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'pending') {
      fetchAffiliates();
    }
  }, [activeTab, fetchAffiliates]);

  const filteredAffiliates = affiliates.filter(affiliate => {
    const searchLower = searchTerm.toLowerCase();
    // Handle different data structures between applications and active affiliates
    const name = affiliate.fullName || affiliate.userId || ''; // Active affiliates might need user lookup
    const email = affiliate.email || '';
    const code = affiliate.referralCode || '';
    
    return name.toLowerCase().includes(searchLower) || 
           email.toLowerCase().includes(searchLower) ||
           code.toLowerCase().includes(searchLower);
  });

  return (
    <div className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="font-montserrat font-bold text-3xl text-neutral-800 mb-2">
              Affiliate Management
            </h1>
            <p className="text-lg text-neutral-600">
              Manage affiliate partners, applications, and payouts
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex space-x-3">
            <Button variant="outline" icon={Download}>
              Export Report
            </Button>
            <Button variant="primary" icon={RefreshCw} onClick={fetchAffiliates}>
              Refresh
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-neutral-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('pending')}
              className={`${
                activeTab === 'pending'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <Clock className="w-4 h-4 mr-2" />
              Pending Applications
            </button>
            <button
              onClick={() => setActiveTab('active')}
              className={`${
                activeTab === 'active'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Active Affiliates
            </button>
            <button
              onClick={() => setActiveTab('rejected')}
              className={`${
                activeTab === 'rejected'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Rejected
            </button>
          </nav>
        </div>

        {/* Content */}
        {activeTab === 'pending' ? (
          <AffiliateApprovalList />
        ) : (
          <div className="space-y-6">
            {/* Filters */}
            <Card className="p-4">
              <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
                  <Input
                    placeholder="Search affiliates..."
                    className="pl-10"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <Button variant="outline" icon={Filter}>
                  Filters
                </Button>
              </div>
            </Card>

            {/* List */}
            <Card className="overflow-hidden">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-2 text-neutral-600">Loading affiliates...</p>
                </div>
              ) : filteredAffiliates.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-neutral-200">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Affiliate
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Referral Code
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Stats
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-neutral-200">
                      {filteredAffiliates.map((affiliate) => (
                        <tr key={affiliate.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold">
                                {(affiliate.fullName || affiliate.userId || 'A').charAt(0).toUpperCase()}
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-neutral-900">
                                  {affiliate.fullName || 'Affiliate User'}
                                </div>
                                <div className="text-sm text-neutral-500">
                                  {affiliate.email || affiliate.userId}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-neutral-100 text-neutral-800">
                              {affiliate.referralCode || 'N/A'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-neutral-900">
                              {affiliate.stats?.totalSignups || 0} Signups
                            </div>
                            <div className="text-sm text-neutral-500">
                              £{(affiliate.stats?.totalEarnings || 0).toFixed(2)} Earned
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              activeTab === 'active' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {activeTab === 'active' ? 'Active' : 'Rejected'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button className="text-neutral-400 hover:text-neutral-600">
                              <MoreVertical className="w-5 h-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-neutral-500">
                  No {activeTab} affiliates found.
                </div>
              )}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};
